import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  Calendar,
  Trash2,
  Image as ImageIcon,
  ArrowRight,
  ExternalLink,
  Code2,
  Ticket,
  PoundSterling,
  Users,
  CalendarClock,
  CreditCard,
  AtSign,
  X as XIcon,
} from "lucide-react";
import PortalShell from "@/components/PortalShell";
import { portalApi, portalMe } from "@/lib/portalApi";

const RECURRENCE_LABEL = {
  none: "One-off",
  every_week: "Weekly",
  monthly_nth: "Monthly",
};

const NTH = { 1: "1st", 2: "2nd", 3: "3rd", 4: "4th", 5: "5th" };

const DAY = { 0: "Mon", 1: "Tue", 2: "Wed", 3: "Thu", 4: "Fri", 5: "Sat", 6: "Sun" };
const jsDayToOurDay = (jsDay) => (jsDay + 6) % 7; // Sunday=0 in JS → 6 in ours

export default function PortalDashboard() {
  const [me, setMe] = useState(null);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openingStripe, setOpeningStripe] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, evRes, stRes] = await Promise.all([
        portalMe(),
        portalApi.get("/portal/events"),
        portalApi.get("/portal/stats"),
      ]);
      setMe(meRes);
      setEvents(evRes.data);
      setStats(stRes.data);
    } catch {
      /* handled */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await portalApi.delete(`/portal/events/${id}`);
      setEvents((es) => es.filter((e) => e.id !== id));
      toast.success("Event deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const openStripeDashboard = async () => {
    setOpeningStripe(true);
    try {
      const { data } = await portalApi.post("/portal/stripe/login-link", {});
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't open your Stripe dashboard."
      );
    } finally {
      setOpeningStripe(false);
    }
  };

  const fmtDate = (d) => {
    try {
      return new Date(d + "T00:00:00").toLocaleDateString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return d;
    }
  };

  const recurrenceLabel = (ev) => {
    if (ev.recurrence === "monthly_nth" && ev.recurrence_nth) {
      const day = jsDayToOurDay(new Date(ev.first_date + "T00:00:00").getDay());
      return `Every ${NTH[ev.recurrence_nth]} ${DAY[day]}`;
    }
    if (ev.recurrence === "every_week") {
      const day = jsDayToOurDay(new Date(ev.first_date + "T00:00:00").getDay());
      return `Every ${DAY[day]}`;
    }
    return RECURRENCE_LABEL[ev.recurrence] || "";
  };

  return (
    <PortalShell me={me} testId="portal-dashboard-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">
              {me?.venue?.name ? `Welcome — ${me.venue.name}` : "Portal"}
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
              Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {me?.venue?.stripe_charges_enabled ? (
              <button
                type="button"
                onClick={openStripeDashboard}
                disabled={openingStripe}
                data-testid="portal-stripe-dashboard-button"
                className="inline-flex items-center gap-2 border border-white/20 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] disabled:opacity-40 px-5 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
              >
                {openingStripe ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <CreditCard size={14} />
                )}
                Stripe Dashboard
                <ExternalLink size={11} className="opacity-70" />
              </button>
            ) : (
              <Link
                to="/portal/payments"
                data-testid="portal-connect-stripe-button"
                className="inline-flex items-center gap-2 border border-[#D9A6F5]/60 text-[#D9A6F5] hover:bg-[#D9A6F5] hover:text-black px-5 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
              >
                <CreditCard size={14} /> Connect Stripe
              </Link>
            )}
            <Link
              to="/portal/embed"
              data-testid="portal-embed-shortcut"
              className="inline-flex items-center gap-2 border border-white/20 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-5 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <Code2 size={14} /> Embed on your site
            </Link>
            <Link
              to="/portal/events/new"
              data-testid="portal-create-event"
              className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <Plus size={14} /> New Event
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center">
            <Loader2 size={20} className="animate-spin text-[#D9A6F5]" />
          </div>
        ) : (
          <>
            <EmailDomainNudge venue={me?.venue} />
            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-12" data-testid="portal-stat-cards">
              <StatCard
                testId="stat-tickets-month"
                icon={<Ticket size={14} />}
                label={`Tickets sold · ${stats?.month || "this month"}`}
                value={stats?.tickets_sold_this_month ?? 0}
                sparkData={(stats?.daily || []).map((d) => d.tickets)}
                sparkTooltip={(stats?.daily || []).map((d) => `${d.date}: ${d.tickets} ticket${d.tickets === 1 ? "" : "s"}`)}
              />
              <StatCard
                testId="stat-revenue-month"
                icon={<PoundSterling size={14} />}
                label={`Revenue · ${stats?.month || "this month"}`}
                value={fmtGBPCompact(stats?.revenue_this_month_gbp ?? 0)}
                sparkData={(stats?.daily || []).map((d) => d.revenue)}
                sparkTooltip={(stats?.daily || []).map((d) => `${d.date}: ${fmtGBPCompact(d.revenue)}`)}
              />
              <StatCard
                testId="stat-upcoming-events"
                icon={<CalendarClock size={14} />}
                label="Upcoming events"
                value={stats?.upcoming_events ?? 0}
                to="#events"
              />
              <StatCard
                testId="stat-contacts"
                icon={<Users size={14} />}
                label="Contacts"
                value={stats?.contacts_count ?? 0}
                to="/portal/contacts"
              />
            </div>

            {/* Quick links row */}
            <div className="mb-10 flex flex-wrap items-center gap-3" data-testid="portal-quick-links">
              <Link
                to="/portal/guestlist"
                data-testid="portal-guestlist-link"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold transition-colors"
              >
                <Users size={12} /> View guestlists
              </Link>
              <Link
                to="/portal/contacts"
                data-testid="portal-contacts-link"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold transition-colors"
              >
                <Users size={12} /> Contacts
              </Link>
              <div className="ml-auto text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3]">
                All-time: <span className="text-[#F5F5F0]">{stats?.tickets_sold_all_time ?? 0}</span> tickets ·{" "}
                <span className="text-[#F5F5F0]">{fmtGBPCompact(stats?.revenue_all_time_gbp ?? 0)}</span>
              </div>
            </div>

            {/* Events section */}
            <div id="events" className="mb-4 flex items-baseline justify-between">
              <h2 className="text-xl md:text-2xl font-black tracking-tight">Your events</h2>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3]">
                {events.length} total
              </div>
            </div>

            {events.length === 0 ? (
              <div className="text-center py-20">
                <div className="inline-flex items-center justify-center h-16 w-16 border border-white/10 mb-6">
                  <Calendar size={22} className="text-[#D9A6F5]" />
                </div>
                <p className="text-[#A3A3A3] mb-8">No events yet. Let&apos;s create your first one.</p>
                <Link
                  to="/portal/events/new"
                  className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
                >
                  <Plus size={14} /> Create your first event
                </Link>
              </div>
            ) : (
              <EventsGrid
                events={events}
                me={me}
                deleteEvent={deleteEvent}
                fmtDate={fmtDate}
                recurrenceLabel={recurrenceLabel}
              />
            )}
          </>
        )}
      </div>
    </PortalShell>
  );
}

function fmtGBPCompact(n) {
  const num = Number(n) || 0;
  if (num >= 10000) {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(num);
  }
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 2,
  }).format(num);
}

function StatCard({ testId, icon, label, value, to, sparkData, sparkTooltip }) {
  const inner = (
    <div className="h-full bg-[#0A0A0A] border border-white/10 hover:border-[#D9A6F5]/40 transition-colors p-4 md:p-5 flex flex-col">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D9A6F5]">
        {icon} <span className="truncate">{label}</span>
      </div>
      <div className="mt-3 text-2xl md:text-3xl font-black tracking-tighter text-[#F5F5F0]">
        {value}
      </div>
      {sparkData && sparkData.length > 1 && (
        <Sparkline data={sparkData} tooltip={sparkTooltip} testId={`${testId}-spark`} />
      )}
    </div>
  );
  if (to && to.startsWith("/")) {
    return (
      <Link to={to} data-testid={testId} className="block">
        {inner}
      </Link>
    );
  }
  if (to && to.startsWith("#")) {
    return (
      <a href={to} data-testid={testId} className="block">
        {inner}
      </a>
    );
  }
  return (
    <div data-testid={testId}>
      {inner}
    </div>
  );
}

function Sparkline({ data, tooltip, testId }) {
  const W = 220;
  const H = 44;
  const P = 2;
  const max = Math.max(1, ...data);
  const min = Math.min(0, ...data);
  const range = Math.max(1, max - min);
  const stepX = (W - P * 2) / (data.length - 1);
  const points = data.map((v, i) => {
    const x = P + i * stepX;
    const y = H - P - ((v - min) / range) * (H - P * 2);
    return [x, y];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`).join(" ");
  const areaPath = `${path} L ${points[points.length - 1][0].toFixed(2)} ${H - P} L ${P} ${H - P} Z`;
  const lastVal = data[data.length - 1];
  const isEmpty = data.every((v) => v === 0);

  return (
    <div className="mt-auto pt-3" data-testid={testId}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="w-full h-10"
        role="img"
        aria-label={`Last ${data.length} days trend`}
      >
        <defs>
          <linearGradient id={`${testId}-grad`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#D9A6F5" stopOpacity={isEmpty ? 0 : 0.35} />
            <stop offset="100%" stopColor="#D9A6F5" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${testId}-grad)`} />
        <path
          d={path}
          fill="none"
          stroke="#D9A6F5"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={isEmpty ? 0.25 : 1}
        />
        {!isEmpty && (
          <circle
            cx={points[points.length - 1][0]}
            cy={points[points.length - 1][1]}
            r="2.2"
            fill="#D9A6F5"
          />
        )}
        {tooltip && (
          <title>
            {tooltip[tooltip.length - 1] || `Latest: ${lastVal}`}
          </title>
        )}
      </svg>
      <div className="mt-1 flex items-center justify-between text-[9px] uppercase tracking-[0.24em] text-[#A3A3A3]">
        <span>{data.length}d trend</span>
        {!isEmpty && <span className="text-[#D9A6F5]">today</span>}
      </div>
    </div>
  );
}

function EventsGrid({ events, me, deleteEvent, fmtDate, recurrenceLabel }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {events.map((ev) => (
        <div
          key={ev.id}
          data-testid={`portal-event-card-${ev.id}`}
          className="group bg-[#0A0A0A] border border-white/10 hover:border-[#D9A6F5]/40 overflow-hidden transition-colors"
        >
          <Link to={`/portal/events/${ev.id}`} className="block">
            <div className="aspect-[16/10] bg-black border-b border-white/10 flex items-center justify-center overflow-hidden">
              {ev.photo_data_uri ? (
                <img src={ev.photo_data_uri} alt={ev.name} className="w-full h-full object-cover" />
              ) : (
                <ImageIcon size={26} className="text-[#A3A3A3]/40" />
              )}
            </div>
            <div className="p-5">
              <h3 className="text-lg font-bold tracking-tight text-[#F5F5F0] truncate">{ev.name}</h3>
              {ev.theme && (
                <div className="mt-1 text-xs uppercase tracking-[0.2em] text-[#D9A6F5]">{ev.theme}</div>
              )}
              <div className="mt-3 flex items-center gap-1.5 text-sm text-[#A3A3A3]">
                <Calendar size={12} className="text-[#D9A6F5]" />
                {fmtDate(ev.first_date)} · {ev.time}
              </div>
              <div className="mt-1 flex items-center justify-between text-xs">
                <span className="text-[#A3A3A3]/80">{recurrenceLabel(ev)}</span>
                {typeof ev.tickets_sold === "number" && ev.tickets_sold > 0 && (
                  <span className="inline-flex items-center gap-1 text-[#D9A6F5]">
                    <Ticket size={11} /> {ev.tickets_sold} sold
                  </span>
                )}
              </div>
            </div>
          </Link>
          <div className="flex items-center justify-between px-5 py-3 border-t border-white/10 text-xs">
            {me?.venue?.slug ? (
              <a
                href={`/v/${me.venue.slug}/${ev.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[#A3A3A3] hover:text-[#D9A6F5] uppercase tracking-[0.2em]"
              >
                Public page <ExternalLink size={11} />
              </a>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-3">
              <Link
                to={`/portal/guestlist?event_id=${ev.id}`}
                data-testid={`portal-event-guestlist-${ev.id}`}
                className="inline-flex items-center gap-1 text-[#A3A3A3] hover:text-[#D9A6F5] uppercase tracking-[0.2em]"
              >
                Guestlist
              </Link>
              <Link
                to={`/portal/events/${ev.id}`}
                className="inline-flex items-center gap-1 text-[#F5F5F0] hover:text-[#D9A6F5] uppercase tracking-[0.2em]"
              >
                Edit <ArrowRight size={11} />
              </Link>
              <button
                type="button"
                onClick={() => deleteEvent(ev.id)}
                data-testid={`portal-event-delete-${ev.id}`}
                className="text-[#A3A3A3] hover:text-red-400 p-1"
                aria-label="Delete event"
              >
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}


const NUDGE_STORAGE_KEY = "swingle:dashboard:email-domain-nudge-dismissed";

function EmailDomainNudge({ venue }) {
  const verified = venue?.email_domain_status === "verified";
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return sessionStorage.getItem(NUDGE_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  if (!venue || verified || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(NUDGE_STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      data-testid="dashboard-email-domain-nudge"
      className="relative mb-8 border border-[#D9A6F5]/40 bg-[#D9A6F5]/[0.06] p-5 md:p-6"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="dashboard-nudge-dismiss"
        className="absolute top-3 right-3 text-[#A3A3A3] hover:text-[#F5F5F0] p-1"
      >
        <XIcon size={14} />
      </button>
      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-10 h-10 border border-[#D9A6F5] flex items-center justify-center text-[#D9A6F5]">
            <AtSign size={16} />
          </div>
          <div className="md:hidden text-[10px] uppercase tracking-[0.28em] text-[#D9A6F5]">
            Deliverability tip
          </div>
        </div>
        <div className="flex-1">
          <div className="hidden md:block text-[10px] uppercase tracking-[0.28em] text-[#D9A6F5] mb-1">
            Deliverability tip
          </div>
          <div className="text-[15px] md:text-base text-[#F5F5F0] leading-snug">
            You&apos;re currently sending customer emails from{" "}
            <span className="font-mono text-[#D9A6F5]">Swingle</span>&apos;s domain.
            Set up your own — buyers see your brand and your inbox reputation stays with you.
          </div>
          <div className="mt-1 text-[11px] text-[#A3A3A3]">Takes about 5 minutes.</div>
        </div>
        <Link
          to="/portal/email"
          data-testid="dashboard-nudge-cta"
          className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-5 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors whitespace-nowrap"
        >
          Set up my domain <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}

